// PageNavMenuItemInstantMessageChannel
// ***********************************************************************************************************************
import GroupIcon from "@mui/icons-material/Group";
import { Box, List, ListItemButton, Paper, Typography } from "@mui/material";
import * as React from "react";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { Base } from "ui/Scripts/source/framework/base";
import { IInstantMessageChannelItem } from "ui/Scripts/source/models/instantMessage/instantMessageChannelItem";
import { Translations } from "ui/Scripts/source/models/translations";
import { AddButton } from "../../framework/Button/AddButton";
import { ConfirmationButton } from "../../framework/Button/ConfirmationButton";
import { NewMessageIcon } from "../../framework/Icon/NewMessageIcon";
import { PageTitle } from "../../framework/Page/PageTitle";
import { EllipsisTypography } from "../../framework/Typography/EllipsisTypography";
import { AppButton } from "ui/Scripts/source/components/framework/Button/AppButton";

export interface IInstantMessageChannelMenuItemProp {
    classes?: boolean;
    isActiveChannel: boolean;
    channel: IInstantMessageChannelItem;
    activeChannelHasUnreadMessagesInList: boolean;
    userCode: string;
    onHandleInvitation: (instantMessageChannelUserId: string, instantMessageChannelId: string, approve: boolean) => void;
}

export class InstantMessageChannelMenuItem extends React.Component<IInstantMessageChannelMenuItemProp, {}> {
    getLatestMessageTimeStr = (date: number): string => {
        const dayStr = Base.timeToDateStr(date);
        const yesterdayDayStr = Base.timeToDateStr(new Date().addDays(-1).getTime());
        const todayDayStr = Base.timeToDateStr(new Date().getTime());
        return dayStr === todayDayStr
            ? Base.timeToTimeStr(date)
            : (dayStr === yesterdayDayStr
                ? Translations.Yesterday
                : dayStr);
    };

    render() {
        const props = this.props;
        const channel = props.channel;
        if (!channel) return null;
        const latestMessageContent = channel.latestMessage?.getText(props.userCode) ?? "";
        const latestMessageText = channel.latestMessage ? channel.latestMessage.modifyUser.name + (latestMessageContent ? ": " + latestMessageContent : "") : "";
        const latestMessageTime = channel.latestMessage ? this.getLatestMessageTimeStr(channel.latestMessage.modifyUser.date) : "";
        const hasNewMessage = (channel.hasUnreadMessages || props.isActiveChannel && props.activeChannelHasUnreadMessagesInList);
        return (
            <ListItemButton selected={props.isActiveChannel} sx={{ height: "auto" }}>
                <Box display="flex" flexDirection="column" flexGrow={2} sx={{ width: "100%" }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" flexGrow={2} sx={{ margin: ".25rem 0" }}>
                        <Box display="flex" alignItems="center">
                            <AvatarImage
                                id={channel.otherUserCode}
                                name={channel.code}
                                icon={channel.isGroup ? <GroupIcon color="info" fontSize="small" /> : undefined}
                                image={channel.getPicture()}
                                tooltip={channel.code}
                                marginRight=".5rem"
                            />
                            <Typography
                                sx={{
                                    fontWeight: hasNewMessage ? 600 : 400,
                                    color: channel.active ? "text.primary" : "text.disabled",
                                    textDecoration: channel.active ? undefined : "line-through",
                                }}
                            >{channel.code}</Typography>
                        </Box>
                        {!!channel.latestMessage &&
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: hasNewMessage ? 600 : 400,
                                    color: channel.active ? "text.primary" : "text.disabled",
                                    textDecoration: channel.active ? undefined : "line-through",
                                }}
                            >
                                {latestMessageTime}
                            </Typography>
                        }
                    </Box>
                    {!!channel.latestMessage &&
                        <Box display="flex" alignItems="start" justifyContent="space-between" flexGrow={2}>
                            <EllipsisTypography
                                variant="caption"
                                title={latestMessageText}
                                dangerouslySetInnerHTML={{ __html: Base.linkifyText(latestMessageText) }}
                                sx={{
                                    margin: "0 0 .25rem 0",
                                    color: channel.active ? "text.primary" : "text.disabled",
                                    textDecoration: channel.active ? undefined : "line-through",
                                }}
                            />
                            {hasNewMessage &&
                                <NewMessageIcon />
                            }
                        </Box>
                    }
                    {!!channel.invitation &&
                        <Box sx={(theme) => ({
                            bgcolor: props.isActiveChannel ? theme.palette.background.paper : theme.palette.background.default,
                            borderRadius: ".25rem",
                            padding: ".5rem",
                            marginTop: ".5rem",
                        })}>
                            <>
                                <Typography variant="caption">
                                    {String.format(Translations.UserParameterHasInvitedYouToJoinInstantMessageChannelParameter, channel.invitation.sender.name)}
                                </Typography>
                                <Box sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                }}>
                                    <AppButton variant="outlined" onClick={() => props.onHandleInvitation(channel.invitation.id, channel.id, false)} size="small" sx={{ marginRight: ".5rem" }}>
                                        {Translations.Reject}
                                    </AppButton>
                                    <ConfirmationButton onClick={() => props.onHandleInvitation(channel.invitation.id, channel.id, true)} size="small">
                                        {Translations.Approve}
                                    </ConfirmationButton>
                                </Box>
                            </>
                        </Box>
                    }
                </Box>
            </ListItemButton>
        );
    }
}

export interface IPageNavMenuItemInstantMessageChannelProp {
    classes?: string;
    channels: IInstantMessageChannelItem[];
    hidden?: boolean;
    isActive: boolean;
    activeChannelHasUnreadMessagesInList: boolean;
    firstLine?: boolean;
    activeChannelId: string;
    userCode: string;
    onAddChannel: () => void;
    onToggleMenuItem: () => void;
    onChangeActiveChannel: (channelId: string) => void;
    onEditChannel: (channelId: string) => void;
    onHandleInvitation: (instantMessageChannelUserId: string, instantMessageChannelId: string, approve: boolean) => void;
}

export class PageNavMenuItemInstantMessageChannel extends React.Component<IPageNavMenuItemInstantMessageChannelProp, {}> {
    render() {
        const props = this.props;
        const channels = props.channels;
        if (!channels) return null;
        return (
            <>
                <PageTitle position="absolute" title={Translations.InstantMessagesMenuTitle} padding="24px 0 24px 24px">
                    {!props.hidden &&
                        <AddButton onClick={props.onAddChannel}>
                            {Translations.AddInstantMessageChannel}
                        </AddButton>
                    }
                </PageTitle>
                <Paper
                    elevation={3}
                    sx={{
                        margin: "136px 0 24px 24px",
                        flexGrow: 2,
                        height: "calc(100vh - 160px)",
                        overflow: "hidden",
                    }}
                >
                    {!props.hidden &&
                        <>
                            <Box sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                bgcolor: "action.hover",
                                padding: "1rem",
                            }}>
                                <Typography variant="h6">{Translations.Channels}</Typography>
                            </Box>
                            <List sx={{ height: "calc(100% - 56px)", overflowY: "auto" }}>
                                {channels.map((channel) =>
                                    <li key={channel.id} onClick={() => props.onChangeActiveChannel(channel.id)} onDoubleClick={() => props.onEditChannel(channel.id)}>
                                        <InstantMessageChannelMenuItem
                                            isActiveChannel={props.activeChannelId === channel.id}
                                            channel={channel}
                                            activeChannelHasUnreadMessagesInList={props.activeChannelHasUnreadMessagesInList}
                                            userCode={props.userCode}
                                            onHandleInvitation={props.onHandleInvitation}
                                        />
                                    </li>
                                )}
                            </List>
                        </>
                    }
                </Paper>
            </>
        );
    }
}